import {
  matrixDefinitionBody,
  type MatrixDefinition,
} from "./report-matrix.js";
export interface BuiltinMatrix {
  slug: string;
  /**
   * The old built-in this one replaced (TRBUD-343), retired when this is
   * seeded. Absent on a built-in that replaced nothing (TRBUD-409's two
   * roadside reports): the seeder then checks and retires nothing for it.
   */
  legacySlug?: string;
  name: string;
  definition: MatrixDefinition;
}
const definitionOf = (definition: unknown): MatrixDefinition =>
  matrixDefinitionBody.parse({
    version: 2,
    mode: "grouped",
    timeframe: "30d",
    ...(definition as object),
  });
const make = (
  legacySlug: string,
  name: string,
  definition: unknown,
): BuiltinMatrix => ({
  slug: `${legacySlug}_v2`,
  legacySlug,
  name,
  definition: definitionOf(definition),
});
/** A built-in with no predecessor: its slug is its own, and nothing is retired. */
const fresh = (slug: string, name: string, definition: unknown): BuiltinMatrix => ({
  slug,
  name,
  definition: definitionOf(definition),
});
export const BUILTIN_MATRIX_DEFINITIONS: readonly BuiltinMatrix[] = [
  make("vendor_spend_ranking", "Vendor spend ranking", {
    base: "money_log",
    timeframe: "ytd",
    columns: [
      { field: "money_log.line_vendor.name" },
      { field: "money_log.amount", aggregate: "sum" },
      { field: "money_log.line_vendor.vendor_id" },
    ],
    filters: [
      { field: "money_log.line_vendor.vendor_id", operator: "not_null" },
    ],
    sort: [{ column: 1, direction: "desc" }],
    chart: { kind: "bar", x: 0, values: [1] },
  }),
  make("labour_roi", "Labour ROI", {
    base: "time_log",
    columns: [
      { field: "time_log.mechanic.display_name" },
      { field: "time_log.worked_date", bucket: "month" },
      { field: "time_log.repair_hours", aggregate: "sum" },
      { field: "time_log.admin_hours", aggregate: "sum" },
      { field: "time_log.repair_labour_value", aggregate: "sum" },
      { field: "time_log.admin_labour_value", aggregate: "sum" },
      { field: "time_log.repair_cost_per_hour", aggregate: "ratio" },
      { field: "time_log.admin_cost_per_hour", aggregate: "ratio" },
      { field: "time_log.mechanic.id" },
    ],
    chart: { kind: "table", values: [2, 3, 4, 5, 6, 7] },
  }),
  make("downtime_by_unit", "Downtime by unit", {
    base: "downtime_log",
    columns: [
      { field: "downtime_log.asset.unit_label" },
      { field: "downtime_log.hours", aggregate: "sum" },
      { field: "downtime_log.asset.asset_id" },
    ],
    sort: [{ column: 1, direction: "desc" }],
    chart: { kind: "bar", x: 0, values: [1] },
  }),
  make("asset_cost_history", "Asset cost history", {
    base: "money_log",
    columns: [
      { field: "money_log.asset.unit_label" },
      { field: "money_log.cost_date", bucket: "month" },
      { field: "money_log.cost_kind" },
      { field: "money_log.amount", aggregate: "sum" },
      { field: "money_log.asset.asset_id" },
    ],
    chart: { kind: "stacked_bar", x: 0, series: 2, values: [3] },
  }),
  make("work_order_cohort", "Work-order cohort", {
    base: "work_orders",
    columns: [
      { field: "work_orders.created_date", bucket: "month" },
      { field: "work_orders.type_name" },
      { field: "work_orders.job_count", aggregate: "sum" },
      { field: "work_orders.completed_job_count", aggregate: "sum" },
      { field: "work_orders.completion_rate", aggregate: "ratio" },
    ],
    chart: { kind: "table", values: [2, 3, 4] },
  }),
  make("departmental_spend", "Spend by work-order type", {
    base: "money_log",
    columns: [
      { field: "money_log.work_order.type_name" },
      { field: "money_log.spend_side" },
      { field: "money_log.amount", aggregate: "sum" },
    ],
    chart: { kind: "stacked_bar", x: 0, series: 1, values: [2] },
  }),
  make("awaiting_final_cost", "Awaiting final cost", {
    base: "work_orders",
    mode: "detail",
    timeframe: "all",
    columns: [
      { field: "work_orders.number" },
      { field: "work_orders.asset.unit_label" },
      { field: "work_orders.primary_vendor.name" },
      { field: "work_orders.days_waiting_final_cost" },
    ],
    filters: [
      {
        field: "work_orders.cost_status",
        operator: "eq",
        value: "awaiting_final_cost",
      },
      { field: "work_orders.is_itemized_ticket", operator: "eq", value: false },
    ],
    sort: [{ column: 3, direction: "desc" }],
  }),
  make("unassigned_aged", "Unassigned aged work", {
    base: "work_orders",
    mode: "detail",
    timeframe: "all",
    columns: [
      { field: "work_orders.number" },
      { field: "work_orders.asset.unit_label" },
      { field: "work_orders.stage_name" },
      { field: "work_orders.days_open" },
    ],
    filters: [
      { field: "work_orders.is_not_closed", operator: "eq", value: true },
      { field: "work_orders.is_unassigned", operator: "eq", value: true },
      {
        field: "work_orders.created_date",
        operator: "older_than_days",
        value: 2,
      },
    ],
    sort: [{ column: 3, direction: "desc" }],
  }),
  make("work_order_category_spend", "Work-order category spend", {
    base: "work_orders",
    timeframe: "ytd",
    columns: [
      { field: "work_orders.category" },
      { field: "work_orders.job_count", aggregate: "sum" },
      { field: "work_orders.spend", aggregate: "sum" },
    ],
    shareMode: "full",
    sort: [{ column: 2, direction: "desc" }],
    chart: { kind: "bar", x: 0, values: [2] },
  }),
  make("purchase_order_invoice_variance", "Purchase-order invoice variance", {
    base: "po_variance",
    columns: [
      { field: "po_variance.po_vendor.name" },
      { field: "po_variance.over_billed", aggregate: "sum" },
      { field: "po_variance.under_billed", aggregate: "sum" },
      { field: "po_variance.cost_entry_id", aggregate: "count" },
      { field: "po_variance.po_vendor.vendor_id" },
    ],
    chart: { kind: "bar", x: 0, values: [1, 2] },
  }),
  // ── TRBUD-409: roadside breakdowns, secondary to the Reports panel ──
  // Kept for the builder's own grouping: open either and regroup it. The
  // panel on Spend & cash is the primary read (Eden, 22 October 2026).
  fresh("roadside_breakdown_costs", "Roadside breakdown costs", {
    base: "money_log",
    mode: "detail",
    timeframe: "ytd",
    columns: [
      { field: "money_log.cost_date" },
      { field: "money_log.work_order.number" },
      { field: "money_log.work_order.asset.unit_label" },
      { field: "money_log.line_vendor.name" },
      { field: "money_log.spend_class" },
      { field: "money_log.spend_side" },
      { field: "money_log.source" },
      { field: "money_log.amount" },
    ],
    filters: [
      { field: "money_log.work_order.is_roadside", operator: "eq", value: true },
    ],
    sort: [{ column: 0, direction: "desc" }],
  }),
  fresh("roadside_jobs_recorded_cost", "Roadside jobs and recorded cost", {
    base: "work_orders",
    mode: "detail",
    timeframe: "ytd",
    columns: [
      { field: "work_orders.number" },
      { field: "work_orders.asset.unit_label" },
      { field: "work_orders.created_date" },
      { field: "work_orders.stage_name" },
      { field: "work_orders.cost_status" },
      { field: "work_orders.spend" },
    ],
    filters: [
      { field: "work_orders.is_roadside", operator: "eq", value: true },
      { field: "work_orders.is_itemized_ticket", operator: "eq", value: false },
    ],
    sort: [{ column: 5, direction: "desc" }],
  }),
];
